import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import FileUploadZone from './components/FileUploadZone';
import AnalysisCreditsDisplay from './components/AnalysisCreditsDisplay';
import { useAuth } from '../../contexts/AuthContext';

const getHostname = (url) => {
  try {
    const hostname = new URL(url).hostname;
    return hostname.replace(/^www\./i, '');
  } catch (error) {
    return '';
  }
};

const ResumeUploadAnalysis = () => {
  const navigate = useNavigate();

  const { user, userProfile } = useAuth();
  const userTier = userProfile?.tier || 'free';
  const creditsRemaining =
    userProfile?.credits_remaining ??
    (userTier === 'free' ? 0 : Infinity);
  const hasCredits = userTier !== 'free' || creditsRemaining > 0;

  // Job context state
  const [jobTitle, setJobTitle] = useState('');
  const [jobCompany, setJobCompany] = useState('');
  const [jobLocation, setJobLocation] = useState('');
  const [jobUrl, setJobUrl] = useState('');
  const [jobDescription, setJobDescription] = useState('');
  const [jobError, setJobError] = useState('');
  const [previousAnalysisId, setPreviousAnalysisId] = useState(null);

  // Check for re-scan data on mount
  useEffect(() => {
    try {
      const rescanDataStr = sessionStorage.getItem('rescan_data');
      if (rescanDataStr) {
        const data = JSON.parse(rescanDataStr);

        // Pre-fill fields if available
        if (data.targetRole) setJobTitle(data.targetRole);
        if (data.jobCompany) setJobCompany(data.jobCompany);
        if (data.jobLocation) setJobLocation(data.jobLocation);
        if (data.jobUrl) setJobUrl(data.jobUrl);
        if (data.jobDescription) setJobDescription(data.jobDescription);
        if (data.previousAnalysisId) setPreviousAnalysisId(data.previousAnalysisId);

        // Clear it so it doesn't persist forever
        sessionStorage.removeItem('rescan_data');
      }
    } catch (error) {
      console.error('Error loading rescan data:', error);
    }
  }, []);

  const jobInput = useMemo(() => {
    const trimmedTitle = jobTitle.trim();
    const trimmedCompany = jobCompany.trim();
    const trimmedLocation = jobLocation.trim();
    const trimmedUrl = jobUrl.trim();
    const trimmedDescription = jobDescription.trim();

    if (!trimmedCompany || !trimmedDescription) {
      return null;
    }

    return {
      source: 'manual',
      jobPosting: {
        title: trimmedTitle || `Opportunity at ${trimmedCompany}`,
        company: trimmedCompany,
        location: trimmedLocation || 'India',
        posted_at: 'recent',
        job_url: trimmedUrl,
        summary: trimmedDescription,
        responsibilities: [],
        requirements: [],
        source_domain: getHostname(trimmedUrl)
      }
    };
  }, [jobTitle, jobCompany, jobLocation, jobUrl, jobDescription]);

  const handleClearJobContext = () => {
    setJobTitle('');
    setJobCompany('');
    setJobLocation('');
    setJobUrl('');
    setJobDescription('');
    setJobError('');
  };

  const handleAnalysisComplete = (payload) => {
    if (!payload) {
      return;
    }
    navigate('/analysis-results-live', { state: payload });
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-foreground mb-2">
              JobAspirant Resume Analysis
            </h1>
            <p className="text-muted-foreground max-w-3xl mx-auto">
              Upload your resume and link it to a live job opportunity. We'll benchmark your skills, surface precise gaps, and recommend a personalised
              learning roadmap with resume improvements.
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8 items-start">
            <div className="lg:col-span-2 space-y-8">
              <section className="bg-card border border-border rounded-lg p-6 space-y-6">
                <div className="flex items-center space-x-2">
                  <Icon name="Target" size={20} className="text-primary" />
                  <h2 className="text-xl font-semibold text-foreground">
                    Define the job you want to target
                  </h2>
                </div>

                <p className="text-sm text-muted-foreground leading-relaxed">
                  Paste the full job description and tell us the company you&rsquo;re targeting. Add
                  an optional title, location, or URL to give the AI extra context.
                </p>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2 md:col-span-2">
                    <label className="text-sm font-medium text-foreground" htmlFor="job-company">
                      Company name (required)
                    </label>
                    <input
                      id="job-company"
                      type="text"
                      required
                      value={jobCompany}
                      onChange={(event) => {
                        setJobCompany(event.target.value);
                        if (jobError) {
                          setJobError('');
                        }
                      }}
                      placeholder="e.g. Razorpay"
                      className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-foreground" htmlFor="job-title">
                      Job title (optional)
                    </label>
                    <input
                      id="job-title"
                      type="text"
                      value={jobTitle}
                      onChange={(event) => setJobTitle(event.target.value)}
                      placeholder="e.g. Node.js Developer"
                      className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-foreground" htmlFor="job-location">
                      Location (optional)
                    </label>
                    <input
                      id="job-location"
                      type="text"
                      value={jobLocation}
                      onChange={(event) => setJobLocation(event.target.value)}
                      placeholder="e.g. Bengaluru, India"
                      className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>

                  <div className="space-y-2 md:col-span-2">
                    <label className="text-sm font-medium text-foreground" htmlFor="job-url">
                      Job URL (optional)
                    </label>
                    <input
                      id="job-url"
                      type="text"
                      value={jobUrl}
                      onChange={(event) => setJobUrl(event.target.value)}
                      placeholder="https://www.linkedin.com/jobs/view/..."
                      className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-foreground" htmlFor="job-description">
                    Paste the job description (required)
                  </label>
                  <textarea
                    id="job-description"
                    value={jobDescription}
                    required
                    onChange={(event) => {
                      setJobDescription(event.target.value);
                      if (jobError) {
                        setJobError('');
                      }
                    }}
                    rows={6}
                    placeholder="Copy & paste the responsibilities / requirements here..."
                    className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                  <p className="text-xs text-muted-foreground">
                    We enrich whatever you paste with live job-board data so the analysis stays
                    grounded in current requirements.
                  </p>
                </div>

                <div className="flex flex-wrap items-center gap-3">
                  <Button variant="outline" onClick={handleClearJobContext}>
                    Clear Job Context
                  </Button>
                  {!jobInput && (
                    <span className="text-xs text-amber-600">
                      Add the company name and job description
                    </span>
                  )}
                </div>
              </section>

              <section className="bg-card border border-border rounded-lg p-6">
                <div className="flex items-center space-x-2 mb-4">
                  <Icon name="Upload" size={20} className="text-primary" />
                  <h2 className="text-xl font-semibold text-foreground">Upload your resume</h2>
                </div>

                <FileUploadZone
                  credits={creditsRemaining === Infinity ? 0 : creditsRemaining}
                  hasCredits={hasCredits}
                  jobInput={jobInput}
                  onAnalysisComplete={handleAnalysisComplete}
                  onJobInputError={setJobError}
                  userTier={userTier}
                  previousAnalysisId={previousAnalysisId}
                />
              </section>

              {jobError && (
                <div className="bg-amber-50 border border-amber-200 text-sm text-amber-700 rounded-lg p-4">
                  {jobError}
                </div>
              )}
            </div>

            <aside className="space-y-6">
              <AnalysisCreditsDisplay
                creditsRemaining={creditsRemaining === Infinity ? 0 : creditsRemaining}
                userTier={userTier}
                onUpgrade={() => navigate('/upgrade')}
              />

              <div className="bg-primary/5 border border-primary/20 rounded-lg p-6">
                <div className="flex items-start space-x-3">
                  <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center flex-shrink-0">
                    <Icon name="Sparkles" size={20} className="text-primary-foreground" />
                  </div>
                  <div className="space-y-2">
                    <p className="text-sm font-semibold text-foreground">
                      What happens after you click analyze?
                    </p>
                    <ul className="text-xs text-muted-foreground space-y-2 list-disc pl-5">
                      <li><span className="font-medium text-foreground">Instant Job Match</span>: See your role fit score immediately.</li>
                      <li><span className="font-medium text-foreground">Your Strengths</span>: Highlighted, job-relevant skills from your resume.</li>
                      <li><span className="font-medium text-foreground">High-Impact Gaps</span>: The top skills to learn next (2-3 that move the needle).</li>
                      <li><span className="font-medium text-foreground">Personalized Learning Path</span>: Curated courses/videos to close gaps fast.</li>
                      <li><span className="font-medium text-foreground">Resume Upgrades</span>: Ready-to-paste bullet improvements for the role.</li>
                      <li><span className="font-medium text-foreground">Proof, Not Guesswork</span>: Live job evidence with citations.</li>

                    </ul>
                  </div>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="bg-card border border-border rounded-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-card-foreground">Quick Actions</h3>
                  <Icon name="Zap" size={20} className="text-primary" />
                </div>

                <div className="space-y-3">
                  <button
                    onClick={() => navigate('/skill-gaps')}
                    className="w-full flex items-center space-x-3 p-3 rounded-lg border border-border hover:border-primary hover:bg-primary/5 transition-all duration-200 group"
                  >
                    <div className="flex-shrink-0 w-10 h-10 bg-orange-500 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                      <Icon name="Target" size={18} color="white" />
                    </div>

                    <div className="flex-1 text-left">
                      <h4 className="text-sm font-semibold text-card-foreground group-hover:text-primary transition-colors">
                        Skill Analysis
                      </h4>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Bridge your skill gaps
                      </p>
                    </div>

                    <Icon name="ChevronRight" size={16} className="text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-all" />
                  </button>

                  <button
                    onClick={() => {
                      alert('AI Career Chat is launching soon for paid users! Stay tuned for updates.');
                    }}
                    className="w-full flex items-center space-x-3 p-3 rounded-lg border border-border hover:border-primary hover:bg-primary/5 transition-all duration-200 group"
                  >
                    <div className="flex-shrink-0 w-10 h-10 bg-purple-500 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                      <Icon name="MessageSquare" size={18} color="white" />
                    </div>

                    <div className="flex-1 text-left">
                      <div className="flex items-center space-x-2">
                        <h4 className="text-sm font-semibold text-card-foreground group-hover:text-primary transition-colors">
                          AI Career Chat
                        </h4>
                        <span className="text-xs px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full font-medium">
                          New
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Get personalized advice
                      </p>
                    </div>

                    <Icon name="ChevronRight" size={16} className="text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-all" />
                  </button>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ResumeUploadAnalysis;